import { trackConfig, type IFramework } from '@/framework';
import { LayerType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { applySnapshotSize, pushHistory, requestTextSVGPreview, updateLayer, validateInputLayer } from '../utils';
import { getTextLayerHost } from '../utils/host';
import { type IShapeText } from './shape-text';

type Options = {
    writeHistory?: boolean;
};

type SpacingKind = 'lineSpacing' | 'letterSpacing';

type Result = Promise<ILayerProps | void>;

export interface ISpacing {
    /**
     * 设置字间距
     * @param layerID - 图层 ID
     * @param value - 字间距（注意：该值 UI 上的值，即底层数据 / 10，在写入图层时需要转为客户端数据）
     * @param options - 配置项
     */
    setLetterSpacing(layerID: string, value: number, options?: Options): Result;

    /**
     * 设置行间距
     * @param layerID - 图层 ID
     * @param value - 行间距（注意：该值 UI 上的值，即底层数据 / 10，在写入图层时需要转为客户端数据）
     * @param options - 配置项
     */
    setLineSpacing(layerID: string, value: number, options?: Options): Result;
}

export class Spacing implements ISpacing {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    /**
     * 使用真实 Text Layer 宿主为间距候选重新排版。
     *
     * @param prevLayer - 修改前的 Text Layer。
     * @param kind - 本次修改的间距字段。
     * @param value - 即将写入的间距值。
     * @returns 可提交的图层与 snapshot；宿主尚未就绪时返回 null。
     */
    private prepare(prevLayer: ILayerProps, kind: SpacingKind, value: number) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData = { ...prevLayerData, [kind]: value };

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (shapeExtension) return { layer: nextLayer, snapshot: null };

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 只有 ready 才能把候选属性与本轮几何作为一个整体提交。
        if (resolution?.status !== 'ready') return null;
        const snapshot = resolution.value;

        nextLayer = applySnapshotSize(nextLayer, snapshot);

        return { layer: nextLayer, snapshot };
    }

    private setGroup(prevLayer: ILayerProps, kind: SpacingKind, value: number, options: Options) {
        const { writeHistory } = options;

        const children = prevLayer.children;
        if (children.length === 0) return;

        const layers: ILayerProps[] = [];
        for (const child of children) {
            const result = this.prepare(child, kind, value);
            if (!result) return prevLayer;

            layers.push(result.layer);
        }

        const nextLayer = { ...prevLayer, children: layers };

        requestTextSVGPreview(this.framework, writeHistory ? 'commit' : 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);
            this.prevLayer = null;
        }

        return nextLayer;
    }

    private async setSingle(prevLayer: ILayerProps, kind: SpacingKind, value: number, options: Options) {
        const { writeHistory } = options;

        const result = this.prepare(prevLayer, kind, value);
        if (!result) return prevLayer;

        let nextLayer = result.layer;

        const { shapeExtension } = prevLayer.data as ITextLayerProps;
        if (shapeExtension && writeHistory) {
            const layer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (layer) nextLayer = layer;
        }

        if (writeHistory) requestTextSVGPreview(this.framework, 'commit', nextLayer);
        else if (result.snapshot) requestTextSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, {
            activate: true,
            dataOnly: Boolean(result.snapshot) && !writeHistory,
        });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);
            this.prevLayer = null;
        }

        return nextLayer;
    }

    private async set(layerID: string, kind: SpacingKind, value: number, options: Options) {
        const prevLayer = (() => {
            if (this.prevLayer && this.prevLayer.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        const nextLayer =
            prevLayer.layerType === LayerType.GROUP_LAYER
                ? this.setGroup(prevLayer, kind, value, options)
                : await this.setSingle(prevLayer, kind, value, options);

        return nextLayer;
    }

    setLetterSpacing(layerID: string, value: number, options: Options = { writeHistory: false }) {
        return this.set(layerID, 'letterSpacing', value, options);
    }

    setLineSpacing(layerID: string, value: number, options: Options = { writeHistory: false }) {
        return this.set(layerID, 'lineSpacing', value, options);
    }
}
